import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, PlusIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import {
  useLearnerAttributes,
  useCreateLearnerAttribute,
} from '../../services/settings';
import { LearnerAttributeKind } from '../../types/settings';

interface AttributeSelectProps {
  kind: LearnerAttributeKind;
  label: string;
  value: string | '';
  onChange: (id: string | '') => void;
  className?: string;
}

const inputBase =
  'w-full px-3.5 py-4 border rounded-lg bg-background-primary text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 transition-colors';
const inputBorder = 'border-[rgba(189,188,201,0.4)]';

/* ── Inline create modal ─────────────────────────────────────────────── */
const QuickCreateModal: React.FC<{
  kind: LearnerAttributeKind;
  label: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}> = ({ kind, label, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateLearnerAttribute(kind);

  const handleSave = () => {
    if (!name.trim()) return;
    setError(null);
    createMutation.mutate(
      { name: name.trim(), description: description.trim() || null },
      {
        onSuccess: (created) => {
          onCreated(created.id);
          onClose();
        },
        onError: (err: any) => {
          const body = err?.response?.data;
          if (body?.error === 'DUPLICATE_NAME') {
            setError(body.message ?? 'A record with this name already exists.');
          } else {
            setError(body?.message ?? 'Failed to create. Please try again.');
          }
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-surface-primary rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-text-primary">Create {label}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <XMarkIcon className="w-4 h-4 text-text-secondary" />
          </button>
        </div>

        {error && (
          <p className="text-xs text-error-600 dark:text-error-400 bg-error-50 dark:bg-error-900/20 px-3 py-2 rounded-lg">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-3">
          <div className="relative">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder=" "
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
              }}
              className="peer w-full h-14 px-3.5 pt-5 pb-2 text-sm text-text-primary bg-surface-elevated border border-border-primary/20 focus:border-primary-500 rounded-xl outline-none transition-colors"
            />
            <label className="absolute left-3.5 top-1.5 text-xs font-medium text-text-secondary pointer-events-none">
              Name *
            </label>
          </div>
          <div className="relative">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder=" "
              className="peer w-full px-3.5 pt-5 pb-2 text-sm text-text-primary bg-surface-elevated border border-border-primary/20 focus:border-primary-500 rounded-xl outline-none transition-colors resize-none"
            />
            <label className="absolute left-3.5 top-1.5 text-xs font-medium text-text-secondary pointer-events-none">
              Description (optional)
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={createMutation.isPending}
            className="px-3 py-2 text-sm font-medium text-text-secondary border border-border-primary/20 rounded-full hover:bg-neutral-50 dark:hover:bg-grey-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!name.trim() || createMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 rounded-full transition-colors disabled:opacity-50"
          >
            <CheckIcon className="w-3.5 h-3.5" />
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── AttributeSelect ─────────────────────────────────────────────────── */
const AttributeSelect: React.FC<AttributeSelectProps> = ({
  kind,
  label,
  value,
  onChange,
  className = '',
}) => {
  const { data, isLoading } = useLearnerAttributes(kind);
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const items = data?.data ?? [];
  const selected = items.find((i) => i.id === value) ?? null;

  // Compute portal panel position aligned to trigger
  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, []);

  const handleToggle = () => {
    if (!open) updatePosition();
    setOpen((v) => !v);
  };

  // Close on outside mousedown (trigger and portal panel are both excluded)
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inPanel = panelRef.current?.contains(target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on scroll so panel stays aligned
  useEffect(() => {
    if (!open) return;
    const handler = () => setOpen(false);
    window.addEventListener('scroll', handler, true);
    return () => window.removeEventListener('scroll', handler, true);
  }, [open]);

  const panel = open && !isLoading ? (
    <div
      ref={panelRef}
      style={panelStyle}
      className="bg-surface-primary border border-border-primary/40 rounded-xl shadow-lg overflow-hidden"
    >
      {/* Options list */}
      <ul className="max-h-48 overflow-y-auto py-1">
        {/* Clear option */}
        <li>
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false); }}
            className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors ${
              value === ''
                ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-350 font-medium'
                : 'text-text-secondary hover:bg-neutral-50 dark:hover:bg-grey-800'
            }`}
          >
            {label}
          </button>
        </li>

        {items.length === 0 ? (
          <li className="px-3.5 py-3 text-sm text-text-secondary italic">
            No {label.toLowerCase()}s yet
          </li>
        ) : (
          items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => { onChange(item.id); setOpen(false); }}
                className={`w-full text-left px-3.5 py-2.5 text-sm flex items-center justify-between transition-colors ${
                  value === item.id
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-350 font-medium'
                    : 'text-text-primary hover:bg-neutral-50 dark:hover:bg-grey-800'
                }`}
              >
                {item.name}
                {value === item.id && <CheckIcon className="w-4 h-4 flex-shrink-0" />}
              </button>
            </li>
          ))
        )}
      </ul>

      {/* Footer: Add button */}
      <div className="border-t border-[rgba(189,188,201,0.3)]">
        <button
          type="button"
          onClick={() => { setOpen(false); setShowCreate(true); }}
          className="w-full flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-primary-600 dark:text-primary-350 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add {label.toLowerCase()}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className={`relative ${className}`}>
        {/* Trigger */}
        <button
          ref={triggerRef}
          type="button"
          onClick={handleToggle}
          className={`${inputBase} ${inputBorder} flex items-center justify-between gap-2 text-left ${
            isLoading ? 'cursor-wait opacity-60' : ''
          }`}
        >
          <span className={selected ? 'text-text-primary' : 'text-text-secondary'}>
            {isLoading ? `Loading ${label.toLowerCase()}s…` : selected ? selected.name : label}
          </span>
          {isLoading ? (
            <span className="w-4 h-4 rounded-full border-2 border-primary-400 border-t-transparent animate-spin flex-shrink-0" />
          ) : (
            <ChevronDownIcon
              className={`w-4 h-4 text-text-secondary flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
            />
          )}
        </button>
      </div>

      {/* Dropdown rendered in document.body to escape stacking contexts */}
      {panel && createPortal(panel, document.body)}

      {showCreate && (
        <QuickCreateModal
          kind={kind}
          label={label}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            onChange(id);
            setShowCreate(false);
          }}
        />
      )}
    </>
  );
};

export default AttributeSelect;
